// What? Catalyst event search results
// Why? To show each event with the company it concerns
// How? A table from `lg` up, cards below it

import React from "react";
import { Sheet, Table, Link, Card, Stack, Box, Typography } from "@mui/joy";

import PageLoading from "../../../components/PageLoading";
import { HeaderCell } from "../components/HeaderCell";
import AutodbTooltipWrapper, {
  AutodbSourceButton,
  AutodbTooltipHost,
} from "../components/AutodbTooltipWrapper";
import { useWideLayout } from "../hooks/useWideLayout";
import { stripSourceRefs } from "../../../utils/stripSourceRefs";
import { compactSummary } from "../utils/compactSummary";
import {
  getFieldInfo,
  formatHeadquartersAddress,
  formatLatestFunding,
  getDevelopmentStage,
  getLeadPipelineAsset,
  getTherapeuticAreas,
} from "../utils/autodbValues";

// Company details hidden from this view while we wait for feedback on whether
// events need them. Turning one back on restores its column and its line on
// the card. `hq` is the company's headquarters, not where the event happens.
const SHOW = {
  hq: false,
  therapeuticAreas: false,
  developmentStage: false,
  employees: false,
  lastFunding: false,
};

// Company, event type, event date, report date and the source icon, plus the
// above.
const COLUMN_COUNT = 5 + Object.values(SHOW).filter(Boolean).length;

// Each event is its own <tbody>: the facts row, then the event text across the
// full width, since a summary squeezed into one column made rows very tall.
// The text sits tight under its facts and slightly indented, with the rule and
// full padding between events, and hovering shades both rows together.
const TWO_ROW_SX = {
  "& tbody > tr:not(:last-of-type) > td": {
    borderBottomWidth: 0,
    paddingBottom: 0.25,
  },
  "& tbody > tr:not(:first-of-type) > td": {
    paddingTop: 0,
    paddingLeft: "calc(var(--TableCell-paddingX) + 16px)",
  },
  "& tbody:not(:last-of-type) > tr:last-of-type > td": {
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
  },
  "& tbody:hover > tr > td": { backgroundColor: "background.level1" },
};

interface CatalystEventSearchResultsProps {
  loading: boolean;
  results: any[];
  onCompanyClick: (companyId: string) => void;
  /** When provided, company names render as real anchors so Cmd/Ctrl/middle-click opens a new tab. */
  hrefForCompany?: (companyId: string) => string;
}

export const CatalystEventSearchResults: React.FC<
  CatalystEventSearchResultsProps
> = ({ loading, results, onCompanyClick, hrefForCompany }) => {
  const isWide = useWideLayout();

  const renderEvent = (catalyst_event: any) => {
    const company = catalyst_event?.value?.company_name;
    const companyId = company?._id ?? company?.reference;

    const name = company?.value?.name?.value;
    const latest_funding = company?.value?.latest_funding?.value;

    const lead_asset = getLeadPipelineAsset(company);
    const tas = getTherapeuticAreas(lead_asset);
    const address = formatHeadquartersAddress(company);
    const developmentStage = getDevelopmentStage(lead_asset);

    const title = catalyst_event?.value?.title?.value;
    const summary = catalyst_event?.value?.summary?.value;

    return (
      <tbody key={catalyst_event._id}>
        <tr>
          <td>
            <Link
              href={
                hrefForCompany && companyId
                  ? hrefForCompany(companyId)
                  : undefined
              }
              component={hrefForCompany && companyId ? "a" : "button"}
              aria-disabled={!companyId}
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
              ) => {
                if (
                  hrefForCompany &&
                  companyId &&
                  (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)
                )
                  return;
                e.preventDefault();
                if (companyId) onCompanyClick(companyId);
              }}
              sx={{
                fontWeight: 500,
                textAlign: "left",
                pointerEvents: companyId ? "auto" : "none",
              }}
            >
              {name ? name : ""}
            </Link>
          </td>
          <td>{catalyst_event?.value?.type?.value ?? ""}</td>
          {SHOW.hq && (
            <td>
              <AutodbTooltipWrapper info={company?.value?.headquarters}>
                {address ?? ""}
              </AutodbTooltipWrapper>
            </td>
          )}
          {SHOW.therapeuticAreas && (
            <td>
              <AutodbTooltipWrapper
                info={getFieldInfo(lead_asset, "therapeutic_area")}
              >
                {tas?.join(", ") ?? ""}
              </AutodbTooltipWrapper>
            </td>
          )}
          {SHOW.developmentStage && (
            <td>
              <AutodbTooltipWrapper
                info={getFieldInfo(lead_asset, "development_stage")}
              >
                {developmentStage}
              </AutodbTooltipWrapper>
            </td>
          )}
          {SHOW.employees && (
            <td>
              <AutodbTooltipWrapper info={company?.value?.number_of_employees}>
                {company?.value?.number_of_employees?.value ?? ""}
              </AutodbTooltipWrapper>
            </td>
          )}
          {SHOW.lastFunding && (
            <td>
              <AutodbTooltipWrapper info={company?.value?.latest_funding}>
                {latest_funding ? formatLatestFunding(latest_funding) : ""}
              </AutodbTooltipWrapper>
            </td>
          )}
          <td>{catalyst_event?.value?.date?.value ?? ""}</td>
          <td>
            {catalyst_event?.source_date
              ? catalyst_event.source_date.includes("T")
                ? catalyst_event.source_date.slice(
                    0,
                    catalyst_event.source_date.indexOf("T"),
                  )
                : catalyst_event.source_date
              : ""}
          </td>
          {/* {renderAttendance(company?.value?.attendance)} */}
          {/* One icon for the whole event, since its facts share one source. */}
          <td style={{ textAlign: "right" }}>
            <AutodbSourceButton info={catalyst_event} />
          </td>
        </tr>
        {(title || summary) && (
          <tr>
            <td colSpan={COLUMN_COUNT}>
              <div>
                <strong>{title}</strong>
              </div>
              <div>{compactSummary(stripSourceRefs(summary ?? ""))}</div>
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  // A render function, not a component: declared in here, a component would be
  // a new type on every render, remounting every card and pulling the source
  // tooltip off the icon it was open on.
  const renderEventCard = (catalyst_event: any) => {
    const company = catalyst_event?.value?.company_name;
    const companyId = company?._id ?? company?.reference;
    const name = company?.value?.name?.value;
    const latest_funding = company?.value?.latest_funding?.value;
    const lead_asset = getLeadPipelineAsset(company);
    const tas = getTherapeuticAreas(lead_asset);
    const address = formatHeadquartersAddress(company);
    const developmentStage = getDevelopmentStage(lead_asset);

    return (
      <Card
        key={catalyst_event._id}
        variant="outlined"
        sx={{
          p: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "sm",
            borderColor: "primary.300",
          },
        }}
      >
        <Stack spacing={1.5}>
          {/* Company Name, and the event's source icon in the corner */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Link
              href={
                hrefForCompany && companyId
                  ? hrefForCompany(companyId)
                  : undefined
              }
              component={hrefForCompany && companyId ? "a" : "button"}
              aria-disabled={!companyId}
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
              ) => {
                if (
                  hrefForCompany &&
                  companyId &&
                  (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)
                )
                  return;
                e.preventDefault();
                if (companyId) onCompanyClick(companyId);
              }}
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                textAlign: "left",
                pointerEvents: companyId ? "auto" : "none",
              }}
            >
              {name || "Unnamed Company"}
            </Link>
            <AutodbSourceButton info={catalyst_event} />
          </Stack>

          {/* Event Details */}
          <Box>
            <Typography
              level="body-xs"
              color="neutral"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              Event
            </Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
              {catalyst_event?.value?.title?.value}
            </Typography>
            {catalyst_event?.value?.summary?.value && (
              <Typography level="body-sm">
                {compactSummary(
                  stripSourceRefs(catalyst_event.value.summary.value),
                )}
              </Typography>
            )}
          </Box>

          {/* Event Type and Date */}
          <Stack direction="row" spacing={3}>
            {catalyst_event?.value?.type?.value && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Event Type
                </Typography>
                <Typography level="body-sm">
                  {catalyst_event.value.type.value}
                </Typography>
              </Box>
            )}
            {catalyst_event?.value?.date?.value && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Event Date
                </Typography>
                <Typography level="body-sm">
                  {catalyst_event.value.date.value}
                </Typography>
              </Box>
            )}
            {catalyst_event?.source_date && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Report Date
                </Typography>
                <Typography level="body-sm">
                  {catalyst_event.source_date.includes("T")
                    ? catalyst_event.source_date.slice(
                        0,
                        catalyst_event.source_date.indexOf("T"),
                      )
                    : catalyst_event.source_date}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Company HQ */}
          {SHOW.hq && address && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Company HQ
              </Typography>
              <AutodbTooltipWrapper info={company?.value?.headquarters}>
                <Typography level="body-sm">{address}</Typography>
              </AutodbTooltipWrapper>
            </Box>
          )}

          {/* Therapeutic Areas */}
          {SHOW.therapeuticAreas && tas.length > 0 && (
            <Box>
              <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Therapeutic Areas
              </Typography>
              <AutodbTooltipWrapper
                info={getFieldInfo(lead_asset, "therapeutic_area")}
              >
                <Typography level="body-sm">{tas.join(", ")}</Typography>
              </AutodbTooltipWrapper>
            </Box>
          )}

          {/* Development Stage and Employee Count */}
          {(SHOW.developmentStage || SHOW.employees) && (
            <Stack direction="row" spacing={3}>
              {SHOW.developmentStage && developmentStage && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Development Stage
                  </Typography>
                  <AutodbTooltipWrapper
                    info={getFieldInfo(lead_asset, "development_stage")}
                  >
                    <Typography level="body-sm">{developmentStage}</Typography>
                  </AutodbTooltipWrapper>
                </Box>
              )}
              {SHOW.employees && company?.value?.number_of_employees?.value && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Employees
                  </Typography>
                  <AutodbTooltipWrapper
                    info={company?.value?.number_of_employees}
                  >
                    <Typography level="body-sm">
                      {company.value.number_of_employees.value}
                    </Typography>
                  </AutodbTooltipWrapper>
                </Box>
              )}
            </Stack>
          )}

          {/* Last Funding */}
          {SHOW.lastFunding &&
            latest_funding &&
            formatLatestFunding(latest_funding) && (
              <Box>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Last Funding
                </Typography>
                <AutodbTooltipWrapper info={company?.value?.latest_funding}>
                  <Typography level="body-sm">
                    {formatLatestFunding(latest_funding)}
                  </Typography>
                </AutodbTooltipWrapper>
              </Box>
            )}

          {/* Attendance hidden - no attendance filter in catalyst events */}
          {/* {company?.value?.attendance &&
            company.value.attendance.length > 0 && (
              <Box>
                <Typography
                  level="body-xs"
                  color="neutral"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Attendance
                </Typography>
                {renderAttendanceCompact(company?.value?.attendance)}
              </Box>
            )} */}
        </Stack>
      </Card>
    );
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      {/* One tooltip serves every wrapped cell on this page. */}
      <AutodbTooltipHost />

      {/* Only the live layout mounts -- see useWideLayout. */}
      {isWide ? (
        <Sheet sx={{ overflow: "auto" }}>
          <Table sx={{ width: "100%", ...TWO_ROW_SX }}>
            <thead>
              <tr>
                <HeaderCell>Company Name</HeaderCell>
                <HeaderCell>Event Type</HeaderCell>
                {SHOW.hq && <HeaderCell>Company HQ</HeaderCell>}
                {SHOW.therapeuticAreas && (
                  <HeaderCell>Therapeutic Areas</HeaderCell>
                )}
                {SHOW.developmentStage && (
                  <HeaderCell>Development Stage</HeaderCell>
                )}
                {SHOW.employees && <HeaderCell>Employee Count</HeaderCell>}
                {SHOW.lastFunding && <HeaderCell>Last Funding</HeaderCell>}
                <HeaderCell>Event Date</HeaderCell>
                <HeaderCell>Report Date</HeaderCell>
                <th aria-label="Source" style={{ width: 40 }} />
                {/* {hasAttendance(results) && <th>Attendance</th>} */}
              </tr>
            </thead>
            {results.map(renderEvent)}
          </Table>
        </Sheet>
      ) : (
        <Stack spacing={2}>{results.map(renderEventCard)}</Stack>
      )}
    </>
  );
};
